import { useEffect, useState, FC } from "react";
import { Formik, Form } from "formik";
import {
  SelectChangeEvent,
  Button,
  Grid,
  Autocomplete,
  TextField,
  FormControl,
  Stack,
  MenuItem,
  Select,
  InputLabel,
  ListItemText
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

import Box from "@mui/material/Box";
import { DataField, Picker } from "../../atoms";
import ConnectionS3Bucket from "../../molecules/connectionTypes/ConnectionS3Bucket";
import ConnectionAzure from "../../molecules/connectionTypes/ConnectionAzure";
import ConnectionDatabase from "../../molecules/connectionTypes/ConnectionDatabase";
import * as functions from "../../shared/functions";
import { IFormDropDown, IFormField } from "../../shared/globalTypes";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import * as constants from "../../../constants/_pickerValues";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../../../store/dataTarget/actions";
import { fetchSubscriptionRequest, postSubscriptionRequest, deleteSubscriptionReq, postSubscriptionReset, fetchSubscriptionReset, deleteSubscriptionReset } from "../../../store/subscriptions/actions";
import { postConnectionRequest, postConnectionReset } from "../../../store/dataSource/actions";
import { IUser } from "../../../store/groups/types";
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  getTargetPendingSelector,
  getTargetSelector,
  postDataSelector,
  putDataSelector,
} from "../../../store/dataTarget/selectors";
import {
  postConnectionSelector,
  postConnectionPendingSelector,
} from "../../../store/dataSource/selectors";
import { getProfileDataSelector } from "../../../store/auth/selectors";
import { getGroupPendingSelector, getGroupUsersSelector } from "../../../store/groups/selectors";
import Skeleton from "@mui/material/Skeleton";
import { fetchGroupUsersRequest } from "../../../store/groups/actions"
import React from "react";
import { fetchTargetDetailsReq } from "../../../store/dataTarget/actions";
import { useNavigate, useParams } from "react-router-dom";
import { ISecret } from "../../../store/dataTarget/types";
import { getRoleClaimsSelector } from "../../../store/auth/selectors";
import { convertUTCDateToLocalDate } from '../../shared/functions';
import { ISubscriptionSearchParams } from "../../../store/subscriptions/types";
import { fetchSubscriptionSelector, deleteSubscriptionSelector, fetchSubscriptionPendingSelector, postSubscriptionSelector } from "../../../store/subscriptions/selectors";
import { fetchTaskConnectionReq, fetchTaskConnectionReset, postTaskConnectionRequest, postTaskConnectionReset } from "../../../store/eventBasedTrigger/actions";
import { getTaskConnectionSelector, postTaskConnectionSelector } from "../../../store/eventBasedTrigger/selectors";
import { ITaskConnectionParams } from "../../../store/eventBasedTrigger/types";
import { IDP_USERS_PRODUCTION } from "../../../constants/common";

// Form fields object

export interface IDataTargetFields {
  name: IFormField;
  description: IFormField;
  owner: IFormField;
  userAccessList: IFormField;
  rttmsCi: IFormField;
  costCenter: IFormField;
  projectCode: IFormField;
  internalOrder: IFormField;
  rttmsRefNumber: IFormField;
  secretName: IFormField;
  endpoint: IFormField;
  database: IFormField;
  extractSource: IFormField;
  port: IFormField;
  criticality: IFormDropDown;
  instanceName: IFormField;
  username: IFormField;
  password: IFormField;
  serviceName: IFormField;
  accountName: IFormField;
  container: IFormField;
  pathPrefix: IFormField;
  sasToken: IFormField;
  accountKey: IFormField;
  s3Bucket: IFormField;
  s3Path: IFormField;
  kmsId: IFormField;
}

// Instantiating Data Target's initial state

let DataTargetInitialState: IDataTargetFields = {
  name: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  description: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  owner: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  criticality: { fieldValue: "", isError: false, errorMsg: "" },
  userAccessList: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  rttmsCi: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  internalOrder: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  costCenter: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  rttmsRefNumber: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  projectCode: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  secretName: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  endpoint: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  database: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  extractSource: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  port: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  instanceName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  username: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  password: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  serviceName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  accountName: {
    fieldValue: "",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  container: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  pathPrefix: {
    fieldValue: "/",
    isError: false,
    errorMsg: "",
    isNumber: false,
  },
  sasToken: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  accountKey: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  s3Bucket: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  s3Path: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
  kmsId: { fieldValue: "", isError: false, errorMsg: "", isNumber: false },
};

let UserAccessListInitial: IUser[];

interface Props {
  mode?: string;
}
export const DataTargetForm: FC<Props> = ({
  mode = constants.MODE.add,
}: Props) => {
  const dispatch = useDispatch();

  const userProfile = useSelector(getProfileDataSelector);
  const idpUsersProduction = useSelector(getGroupUsersSelector(IDP_USERS_PRODUCTION));
  const idpUsersProductionPending = useSelector(getGroupPendingSelector(IDP_USERS_PRODUCTION));
  const targetDetails = useSelector(getTargetSelector);
  const postTaskConnection = useSelector(postTaskConnectionSelector);
  const getTaskConnection = useSelector(getTaskConnectionSelector)
  const putTargetDetails = useSelector(putDataSelector);
  const postTargetDetails = useSelector(postDataSelector);
  const loadTargetDetails = useSelector(getTargetPendingSelector);
  const postSubscriptionSuccess = useSelector(postSubscriptionSelector)
  const deleteSubscriptionSuccess = useSelector(deleteSubscriptionSelector)
  const postConnection = useSelector(postConnectionSelector);
  const postConnectionPending = useSelector(postConnectionPendingSelector);
  const [loadIcon, setLoadIcon] = useState(false);
  const subscriptionList = useSelector(fetchSubscriptionSelector);
  const subscriptionPendingList = useSelector(fetchSubscriptionPendingSelector);
  const [subscriptionPending, setSubscriptionPending] = useState(false);
  const [dataTargetFormData, setDataTargetFormData] = useState(
    DataTargetInitialState
  );
  const [connection, setConnection] = useState("");
  const [connectionSuccess, setConnectionSuccess] = useState(false);
  const [connectionDetailsValue, setConnectionDetailsValue] = useState("");

  const [displaySaveBtn, setDisplaySaveBtn] = useState(false);
  const [displayTestBtn, setDisplayTestBtn] = useState(false);
  const [displayEditBtn, setDisplayEditBtn] = useState(false);

  const [validationErrors, setValidationErrors] = useState(true);
  const [secretNameValue, setSecretNameValue] = useState("");
  const [userAccessIDs, setUserAccessIDs] = useState(UserAccessListInitial);

  const [createdBy, setCreatedBy] = useState("");
  const [createdOn, setCreatedOn] = useState("");
  const [lastUpdatedBy, setLastUpdatedBy] = useState("");
  const [lastUpdatedOn, setLastUpdatedOn] = useState("");
  const [ownerValue, setOwnerValue] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [postTaskConnectionId, setPostTaskConnectionId] = useState<any>();
  const [taskConnectionId, setTaskConnectionId] = useState<any>();

  const [targetNavigation, setTargetNavigation] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { targetId } = useParams();

  //View mode connection details fields
  const [database, setDatabase] = useState<any>()
  const [endpoint, setEndpoint] = useState<any>()
  const [port, setPort] = useState<any>()
  const [username, setUsername] = useState<any>()
  const [bucket, setBucket] = useState<any>()
  const [path, setPath] = useState<any>()
  const [shareName, setShareName] = useState<any>()

  const navigate = useNavigate();

  // Populate Access Controls drop down

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!idpUsersProductionPending && idpUsersProduction.length <= 0) {
      dispatch(fetchGroupUsersRequest(IDP_USERS_PRODUCTION));
    }

    resetFields();
    dispatch(actions.fetchTargetDetailsReset())
    dispatch(postConnectionReset());
    dispatch(postTaskConnectionReset());
    dispatch(fetchTaskConnectionReset());
    dispatch(postSubscriptionReset());
    dispatch(fetchSubscriptionReset())
    dispatch(deleteSubscriptionReset())

  }, []);

  /*****SUBSCRIPTION *****/


  useEffect(() => {
    setSubscriptionPending(subscriptionPendingList)
  }, [subscriptionPendingList]);


  useEffect(() => {
    window.scrollTo(0, 0);
    if (ownerValue && mode === constants.MODE.view && taskConnectionId && taskConnectionId.length > 0) {
      const params: ISubscriptionSearchParams = {
        taskConnectionId: taskConnectionId[0].id,
        subscriber: ownerValue
      };
      dispatch(fetchSubscriptionRequest(params));
    }
  }, [ownerValue, taskConnectionId]);



  // step 1: set task connection when the user clicks subscribe for the first time
  useEffect(() => {
    setPostTaskConnectionId(postTaskConnection);
  }, [postTaskConnection]);

  // fetch all task connections after step 1
  useEffect(() => {
    if (targetDetails && postTaskConnectionId) {
      const subscriptionPayload = {
        "taskConnectionId": postTaskConnectionId.taskConnectionId,
        "subscriber": userProfile?.mail,
        "owner": dataTargetFormData.owner.fieldValue,
        "description": dataTargetFormData.description.fieldValue

      }
      dispatch(postSubscriptionRequest(subscriptionPayload));
    }
  }, [postTaskConnectionId, targetDetails]);

  // fetch all task connections for existing task connection id
  useEffect(() => {
    setTaskConnectionId(getTaskConnection)
  }, [getTaskConnection]);




  const subscriptionClicked = () => {

    if (taskConnectionId && taskConnectionId.length > 0) {
      const subscriptionPayload = {
        "taskConnectionId": taskConnectionId[0].id,
        "subscriber": userProfile?.mail,
        "owner": dataTargetFormData.owner.fieldValue,
        "description": dataTargetFormData.description.fieldValue

      }
      dispatch(postSubscriptionRequest(subscriptionPayload));
    }
    else {

      // creating task connection
      let connectionTaskpayload = {
        "layerName": dataTargetFormData.name.fieldValue,
        "description": dataTargetFormData.description.fieldValue,
        "refConnectionType": "Load_Destination",
        "criticality": Number(dataTargetFormData.criticality.fieldValue)
      }
      dispatch(postTaskConnectionRequest(connectionTaskpayload));
    }


  };

  // if subscription is successful
  useEffect(() => {

    if (postSubscriptionSuccess?.subscriptionId) {
      setSubscribed(true);
      setSubscriptionId(postSubscriptionSuccess?.subscriptionId)
    }
  }, [postSubscriptionSuccess]);

  // if subscription deleted successfully
  useEffect(() => {

    if (deleteSubscriptionSuccess?.isSuccess) {
      setSubscribed(false);
    }
  }, [deleteSubscriptionSuccess]);

  const subscriptionDeleted = () => {
    dispatch(deleteSubscriptionReq(subscriptionId, "target"));
  };

  // has the user subscribed to this Extractor
  useEffect(() => {
    if (!subscriptionPendingList && subscriptionList && subscriptionList.length > 0 && subscriptionList[0].enabled) {
      setSubscriptionId(subscriptionList[0].subscriptionId)
      setSubscribed(true)
    } else {
      setSubscribed(false)
    }
  }, [subscriptionList, subscriptionPendingList]);


  // ******** END SUBSCRIPTION ***********


  // roles  - IDP.Admin or IDP.User
  const roles: string[] = useSelector(getRoleClaimsSelector);

  useEffect(() => {
    if (roles && roles.length > 0) {
      setIsAdmin(roles.includes("IDP.Admin") ? true : false);
    }
  }, [roles])

  // reset fields
  const resetFields = () => {
    dispatch(actions.fetchTargetDetailsReset())
    dispatch(postConnectionReset());
    dispatch(postTaskConnectionReset());
    dispatch(fetchTaskConnectionReset());
    dispatch(postSubscriptionReset());
    dispatch(fetchSubscriptionReset())

    dataTargetFormData.name.fieldValue = ""
    if (dataTargetFormData.name.fieldValue === "")
      dataTargetFormData.name.isError = false;
    if (dataTargetFormData.description.fieldValue === "")
      dataTargetFormData.description.isError = false;
    if (dataTargetFormData.owner.fieldValue === "")
      dataTargetFormData.owner.isError = false;
    if (dataTargetFormData.userAccessList.fieldValue === "")
      dataTargetFormData.userAccessList.isError = false;



    if (
      connection === constants.CONNECTION_TYPES.mssql ||
      connection === constants.CONNECTION_TYPES.oracle ||
      connection === constants.CONNECTION_TYPES.postGres
    ) {
      if (connection === constants.CONNECTION_TYPES.mssql) {
        if (dataTargetFormData.database.fieldValue === "")
          dataTargetFormData.database.isError = false;
        // if (dataTargetFormData.instanceName.fieldValue === "")
        //   dataTargetFormData.instanceName.isError = false;
        if (dataTargetFormData.port.fieldValue === "")
          dataTargetFormData.port.isError = false;
        if (dataTargetFormData.password.fieldValue === "")
          dataTargetFormData.password.isError = false;
      } else if (connection === constants.CONNECTION_TYPES.oracle) {
        if (dataTargetFormData.extractSource.fieldValue === "")
          dataTargetFormData.extractSource.isError = false;
        if (dataTargetFormData.database.fieldValue === "")
          dataTargetFormData.database.isError = false;
        if (dataTargetFormData.serviceName.fieldValue === "")
          dataTargetFormData.serviceName.isError = false;
        if (dataTargetFormData.port.fieldValue === "")
          dataTargetFormData.port.isError = false;
        if (dataTargetFormData.password.fieldValue === "")
          dataTargetFormData.password.isError = false;
      } else if (connection === constants.CONNECTION_TYPES.postGres) {
        // if (dataTargetFormData.instanceName.fieldValue === "")
        //   dataTargetFormData.instanceName.isError = false;
        if (dataTargetFormData.port.fieldValue === "")
          dataTargetFormData.port.isError = false;
        if (dataTargetFormData.password.fieldValue === "")
          dataTargetFormData.password.isError = false;
      } else {
        if (dataTargetFormData.database.fieldValue === "")
          dataTargetFormData.database.isError = false;
      }

      if (dataTargetFormData.endpoint.fieldValue === "")
        dataTargetFormData.endpoint.isError = false;
      if (dataTargetFormData.username.fieldValue === "")
        dataTargetFormData.username.isError = false;
    }

    if (connection === constants.CONNECTION_TYPES.s3Buckets) {
      if (dataTargetFormData.s3Bucket.fieldValue === "")
        dataTargetFormData.s3Bucket.isError = false;
      if (dataTargetFormData.s3Path.fieldValue === "")
        dataTargetFormData.s3Path.isError = false;
    }

    if (connection === constants.CONNECTION_TYPES.azure) {
      if (dataTargetFormData.accountName.fieldValue === "")
        dataTargetFormData.accountName.isError = false;
      if (dataTargetFormData.container.fieldValue === "")
        dataTargetFormData.container.isError = false;
      if (dataTargetFormData.pathPrefix.fieldValue === "")
        dataTargetFormData.pathPrefix.isError = false;
      if (dataTargetFormData.sasToken.fieldValue === "")
        dataTargetFormData.sasToken.isError = false;
      if (dataTargetFormData.accountKey.fieldValue === "")
        dataTargetFormData.accountKey.isError = false;
    }
    setDataTargetFormData(dataTargetFormData);
  };

  useEffect(() => {
    //When target ID is available, when it is either VIEW or EDIT
    if (mode === constants.MODE.edit && targetId) {
      dispatch(fetchTargetDetailsReq(Number(targetId)));
      setDisplaySaveBtn(true);

    }

    if (mode === constants.MODE.view && targetId) {
      dispatch(fetchTargetDetailsReq(Number(targetId)));
      setDisplaySaveBtn(false);

    }

  }, [targetId, mode]);

  //After edit navigate to back to List screen
  useEffect(() => {
    if (
      putTargetDetails !== null &&
      putTargetDetails !== undefined &&
      targetNavigation
    ) {
      navigate(`/ViewDataTarget/${targetId}`);
      setTargetNavigation(false);
    }
  }, [putTargetDetails]);

  //After  save navigate to back to List screen
  useEffect(() => {
    if (
      postTargetDetails !== null &&
      postTargetDetails !== undefined &&
      targetNavigation
    ) {
      navigate("/DataTargetList");
      setTargetNavigation(false);
    }
  }, [postTargetDetails]);




  useEffect(() => {
    if (
      (mode === constants.MODE.edit || mode === constants.MODE.view) &&
      targetDetails
    ) {
      dataTargetFormData.name.fieldValue = targetDetails?.name;
      dataTargetFormData.description.fieldValue =
        targetDetails?.businessMetadata?.description;
      dataTargetFormData.costCenter.fieldValue =
        targetDetails?.businessMetadata?.costCentre;
      dataTargetFormData.projectCode.fieldValue =
        targetDetails?.businessMetadata?.projectCode ?? "";
      dataTargetFormData.rttmsCi.fieldValue =
        targetDetails?.businessMetadata?.rttmsCI ?? "";
      dataTargetFormData.rttmsRefNumber.fieldValue =
        targetDetails?.businessMetadata?.rttmsReferenceNumber ?? "";
      dataTargetFormData.internalOrder.fieldValue =
        targetDetails?.businessMetadata?.internalOrder ?? "";
      dataTargetFormData.owner.fieldValue =
        targetDetails?.businessMetadata?.owner;
      dataTargetFormData.userAccessList.fieldValue =
        targetDetails?.businessMetadata?.userAccessList;
      dataTargetFormData.secretName.fieldValue = targetDetails?.secretName;
      dataTargetFormData.criticality.fieldValue = targetDetails.criticality ? targetDetails.criticality.toString() : ''
      setUserAccessIDs(
        functions.getStewardIDs(targetDetails?.businessMetadata?.userAccessList)
      );
      setConnection(
        functions.generateConnectionTarget(targetDetails.layerType)
      );
      setLastUpdatedBy(targetDetails?.updatedBy);
      var newDateFormat = convertUTCDateToLocalDate(targetDetails?.updatedOn);
      if (newDateFormat !== null) setLastUpdatedOn(newDateFormat.toString());
      setCreatedBy(targetDetails?.createdBy);
      setCreatedOn(convertUTCDateToLocalDate(targetDetails?.createdOn).toString());
    } else {
      dataTargetFormData.name.fieldValue = "";
      dataTargetFormData.description.fieldValue = "";
      dataTargetFormData.costCenter.fieldValue = "";
      dataTargetFormData.projectCode.fieldValue = "";
      dataTargetFormData.rttmsCi.fieldValue = "";
      dataTargetFormData.rttmsRefNumber.fieldValue = "";
      dataTargetFormData.internalOrder.fieldValue = "";
      dataTargetFormData.owner.fieldValue = "";
      dataTargetFormData.userAccessList.fieldValue = "";
      dataTargetFormData.secretName.fieldValue = "";
    }
    if (mode === constants.MODE.view
      && dataTargetFormData.owner.fieldValue.toLowerCase() === userProfile?.mail.toLowerCase()) {
      if (targetDetails?.connectionDetails) { setConnectionDetailsOnView(targetDetails?.connectionDetails); }
      setDisplayEditBtn(true);
    } else {
      setDisplayEditBtn(false);
    }
  }, [targetDetails, mode, userProfile]);


  useEffect(() => {
    if (mode === constants.MODE.view && targetDetails) {
      const layerNameParams: ITaskConnectionParams = { layerName: targetDetails?.name }
      dispatch(fetchTaskConnectionReq(layerNameParams));
    }
  }, [targetDetails]);

  const setConnectionDetailsOnView = (connValue: string) => {

    if (connValue) {
      try {
        let jsonString = connValue.replaceAll(`'`, `"`)

        const convertConnectionDetails = JSON.parse(jsonString)

        convertConnectionDetails && Object.entries(convertConnectionDetails).forEach(([key, value]) => {
          if (key === "database") {
            setDatabase(value)
          }
          if (key === "port") {
            setPort(value)
          }
          if (key === "userName") {
            setUsername(value)
          }
          if (key === "endpoint") {
            setEndpoint(value)
          }
          if (key === "bucket") {
            setBucket(value)
          }
          if (key === "path") {
            setPath(value)
          }
          if (key === "shareName") {
            setShareName(value)
          }
        })
      } catch (error) {
      }
    }
  };

  // Handling owner changes

  useEffect(() => {
    dataTargetFormData.owner.fieldValue = ownerValue;
    setDataTargetFormData(dataTargetFormData);
  }, [ownerValue]);

  useEffect(() => {
    if (userProfile) {
      setOwnerValue(userProfile["userPrincipalName"]);
    }
  }, [userProfile]);




  const changeConnection = (event: SelectChangeEvent<string>) => {
    let connectionType: string = event.target.value;
    setConnection(connectionType);
    setDisplayTestBtn(false);
    setConnectionSuccess(false);
    setDisplaySaveBtn(false);
  };

  // Update fieldValue when it is changed; needed to update the UI of the text fields, text areas

  const handleChange = (event: {}) => {
    const e = event as React.ChangeEvent<HTMLInputElement>;

    setDataTargetFormData({
      ...dataTargetFormData,
      [e.target.name]:
        e.target.name === "name"
          ? functions.getFormField(
            functions.cleanName(e.currentTarget.value, "")
          )
          : functions.getFormField(e.currentTarget.value),
    });
  };



  const handleDropdownChange = (e: SelectChangeEvent<string>) => {
    setDataTargetFormData({
      ...dataTargetFormData,
      [e.target.name]: functions.getFormField(e.target.value),
    });
  };

  // Test the connection, validates the required fields prior to testing

  const testConnection = () => {
    validate();

    // if (validate() === true) {
    //   setConnectionSuccess(false);
    // } else {
    //   checkConnection();
    //   setConnectionDetails(connection);
    // }
  };

  const checkConnection = () => {
    dispatch(postConnectionRequest(getConnectionPayload()));
    setLoadIcon(true);
  };

  useEffect(() => {
    if (validationErrors === false) {
      if (
        (connection === constants.CONNECTION_TYPES.mssql ||
          connection === constants.CONNECTION_TYPES.postGres) &&
        connectionSuccess !== false
      ) {
        checkConnection();
      }

      if (connectionSuccess === true) {
        submitDataTarget();
      }
    }
  }, [validationErrors]);

  useEffect(() => {
    setConnectionSuccess(postConnection ? postConnection?.succeeded : false);
    if (postConnection?.succeeded === true) setDisplaySaveBtn(true);
  }, [postConnection]);

  const goBack = () => {
    resetFields();
    dispatch(postConnectionReset());
    window.history.back();
  };

  const viewTargets = (event: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/DataLoaderList/Target/${dataTargetFormData.name.fieldValue}`;
    if (event.ctrlKey) {
      window.open(url, '_blank')
    }
    else {
      navigate(url);
    }
  };

  // Validate the required fields of the form
  // onBlur won't be able to determine if fields are empty unless the focus was on a field
  // Conditions are set on which to validate, depending on the connection type
  // Check if connection is mssql, postgres, oracle
  // If fields are empty, then display alert and validation
  // Else move to check connection
  const validate = () => {
    if (connection === constants.CONNECTION_TYPES.mssql || connection === constants.CONNECTION_TYPES.postGres || connection === constants.CONNECTION_TYPES.redShift && mode === constants.MODE.add) {
      setSecretName(connection);
      if (dataTargetFormData.name.fieldValue === "" || dataTargetFormData.description.fieldValue === "" ||
        dataTargetFormData.owner.fieldValue === "" || dataTargetFormData.database.fieldValue === "" || dataTargetFormData.port.fieldValue === "" || dataTargetFormData.password.fieldValue === "") {
        if (connection === constants.CONNECTION_TYPES.mssql) {
          if (dataTargetFormData.database.fieldValue === "")
            dataTargetFormData.database.isError = true;
          if (dataTargetFormData.name.fieldValue === "")
            dataTargetFormData.name.isError = true;
          if (dataTargetFormData.description.fieldValue === "")
            dataTargetFormData.description.isError = true;
          if (dataTargetFormData.owner.fieldValue === "")
            dataTargetFormData.owner.isError = true;
          // if (dataTargetFormData.instanceName.fieldValue === "")
          //   dataTargetFormData.instanceName.isError = true;
          if (dataTargetFormData.port.fieldValue === "")
            dataTargetFormData.port.isError = true;
          if (dataTargetFormData.password.fieldValue === "")
            dataTargetFormData.password.isError = true;
        } else if (connection === constants.CONNECTION_TYPES.postGres) {
          if (dataTargetFormData.database.fieldValue === "")
            dataTargetFormData.database.isError = true;
          if (dataTargetFormData.name.fieldValue === "")
            dataTargetFormData.name.isError = true;
          if (dataTargetFormData.description.fieldValue === "")
            dataTargetFormData.description.isError = true;
          if (dataTargetFormData.owner.fieldValue === "")
            dataTargetFormData.owner.isError = true;
          if (dataTargetFormData.port.fieldValue === "")
            dataTargetFormData.port.isError = true;
          if (dataTargetFormData.password.fieldValue === "")
            dataTargetFormData.password.isError = true;
        } else {
          if (dataTargetFormData.database.fieldValue === "")
            dataTargetFormData.database.isError = true;
          if (dataTargetFormData.name.fieldValue === "")
            dataTargetFormData.name.isError = true;
          if (dataTargetFormData.description.fieldValue === "")
            dataTargetFormData.description.isError = true;
          if (dataTargetFormData.owner.fieldValue === "")
            dataTargetFormData.owner.isError = true;
        }

        if (dataTargetFormData.endpoint.fieldValue === "")
          dataTargetFormData.endpoint.isError = true;
        if (dataTargetFormData.username.fieldValue === "")
          dataTargetFormData.username.isError = true;
        if (dataTargetFormData.criticality.fieldValue === "")
          dataTargetFormData.criticality.isError = true;
        alert("Required fields cannot be empty")
        setDataTargetFormData(dataTargetFormData);

      }
      else {
        //submitDataTarget();
        checkConnection();
      }

    } else {

      // ADLS and mode is add
      if (connection === constants.CONNECTION_TYPES.azure && mode === constants.MODE.add) {
        setSecretName(connection);

        if (dataTargetFormData.accountName.fieldValue === "" || dataTargetFormData.container.fieldValue === "" || dataTargetFormData.name.fieldValue === "" ||
          dataTargetFormData.description.fieldValue === "") {
          if (dataTargetFormData.name.fieldValue === "")
            dataTargetFormData.name.isError = true;
          if (dataTargetFormData.description.fieldValue === "")
            dataTargetFormData.description.isError = true;
          if (dataTargetFormData.owner.fieldValue === "")
            dataTargetFormData.owner.isError = true;
          if (dataTargetFormData.accountName.fieldValue === "")
            dataTargetFormData.accountName.isError = true;
          if (dataTargetFormData.container.fieldValue === "")
            dataTargetFormData.container.isError = true;
          if (
            dataTargetFormData.sasToken.fieldValue === "" &&
            dataTargetFormData.accountKey.fieldValue === ""
          ) {
            dataTargetFormData.sasToken.isError = true;
            dataTargetFormData.accountKey.isError = true;
          }
          setDataTargetFormData(dataTargetFormData);
          alert("Required fields cannot be empty")
        }
        else {
          submitDataTarget();
        }

      }

      if (connection === constants.CONNECTION_TYPES.azure && mode === constants.MODE.edit) {
        submitDataTarget();
      }

      // Amazon and mode is add
      if (connection === constants.CONNECTION_TYPES.s3Buckets && mode === constants.MODE.add) {
        setSecretName(connection);
        if (dataTargetFormData.s3Bucket.fieldValue === "" || dataTargetFormData.s3Path.fieldValue === "" || dataTargetFormData.kmsId.fieldValue === "" || dataTargetFormData.name.fieldValue === "" ||
          dataTargetFormData.description.fieldValue === "") {
          if (dataTargetFormData.s3Bucket.fieldValue === "")
            dataTargetFormData.s3Bucket.isError = true;
          if (dataTargetFormData.s3Path.fieldValue === "")
            dataTargetFormData.s3Path.isError = true;
          if (dataTargetFormData.kmsId.fieldValue === "")
            dataTargetFormData.kmsId.isError = true;
          if (dataTargetFormData.name.fieldValue === "")
            dataTargetFormData.name.isError = true;
          if (dataTargetFormData.description.fieldValue === "")
            dataTargetFormData.description.isError = true;
          if (dataTargetFormData.owner.fieldValue === "")
            dataTargetFormData.owner.isError = true;


          setDataTargetFormData(dataTargetFormData);
          alert("Required fields cannot be empty")
        }
        else {
          submitDataTarget();
        }
      }

      if (connection === constants.CONNECTION_TYPES.s3Buckets && mode === constants.MODE.edit) {
        submitDataTarget();
      }
    }

  };

  useEffect(() => {
    // Display save button for S3, Azure, WFS and test connection button for the rest
    if (
      mode !== constants.MODE.view &&
      (connection === constants.CONNECTION_TYPES.s3Buckets ||
        connection === constants.CONNECTION_TYPES.azure ||
        // connection === constants.CONNECTION_TYPES.redShift ||
        connection === constants.CONNECTION_TYPES.windowsFileshare)
    ) {
      setDisplaySaveBtn(true);
      setConnectionSuccess(true);
    }

    if (
      mode === constants.MODE.add &&
      connection !== "" &&
      connection !== constants.CONNECTION_TYPES.s3Buckets &&
      // connection !== constants.CONNECTION_TYPES.redShift &&
      connection !== constants.CONNECTION_TYPES.azure &&
      connection !== constants.CONNECTION_TYPES.windowsFileshare
    ) {
      setDisplayTestBtn(true);
    }
  }, [connection]);

  // Generate the secret name

  const setSecretName = (connectionType: string) => {
    let secretName: string = "";

    switch (connection) {
      case constants.CONNECTION_TYPES.s3Buckets:
        secretName = functions.generateSecretNameS3(
          constants.SCREEN_TYPES.target,
          dataTargetFormData.s3Bucket.fieldValue,
          dataTargetFormData.s3Path.fieldValue
        );
        break;
      case constants.CONNECTION_TYPES.azure:
        secretName = functions.generateSecretNameADLS(
          dataTargetFormData.accountName.fieldValue,
          dataTargetFormData.container.fieldValue
        );
        break;
      default:
        secretName = functions.generateSecretName(
          connectionType,
          constants.SCREEN_TYPES.target,
          dataTargetFormData.endpoint.fieldValue,
          dataTargetFormData.database.fieldValue
        );
        break;
    }

    dataTargetFormData.secretName.fieldValue = secretName;

    setSecretNameValue(secretName);
    setDataTargetFormData(dataTargetFormData);
  };

  // Generate the connectionDetails string

  const setConnectionDetails = (connection: string) => {
    let connDetails: string = "";

    switch (connection) {
      case constants.CONNECTION_TYPES.mssql:
        connDetails =
          `{'endpoint':'${dataTargetFormData.endpoint.fieldValue}', 'database':'${dataTargetFormData.database.fieldValue}',` +
          `'port':'${dataTargetFormData.port.fieldValue}',` +
          `'userName':'${dataTargetFormData.username.fieldValue}'}`;

        break;

      case constants.CONNECTION_TYPES.s3Buckets:
        connDetails = `{'bucket':'${dataTargetFormData.s3Bucket.fieldValue}','path':'${dataTargetFormData.s3Path.fieldValue}'}`;

        break;

      case constants.CONNECTION_TYPES.redShift:
        connDetails = `{'endpoint': '${dataTargetFormData.endpoint.fieldValue}', 'database': '${dataTargetFormData.database.fieldValue}'}`;

        break;
      //  'synapseConnectionString': 'Server=${dataTargetFormData.endpoint.fieldValue};Database=${dataTargetFormData.database.fieldValue};UserName=${dataTargetFormData.username.fieldValue};Password=${dataTargetFormData.password.fieldValue};Port=${dataTargetFormData.port.fieldValue};'}`;


      case constants.CONNECTION_TYPES.azure:
        connDetails = dataTargetFormData.accountKey.fieldValue !== "" ? `${dataTargetFormData.accountName.fieldValue}:accountKey`
          : `${dataTargetFormData.sasToken.fieldValue}:sasUri`
        // connDetails = dataTargetFormData.accountKey.fieldValue !== "" ? `{'type':'AzureBlobStorage','appConnectionString':'','synapseConnectionString': AccountName=${dataTargetFormData.accountName.fieldValue};Container=${dataTargetFormData.container.fieldValue};Path=${dataTargetFormData.pathPrefix.fieldValue};AccountKey=${dataTargetFormData.accountKey.fieldValue};`
        // : `{'type':'AzureBlobStorage','appConnectionString':'','synapseConnectionString': AccountName=${dataTargetFormData.accountName.fieldValue};Container=${dataTargetFormData.container.fieldValue};Path=${dataTargetFormData.pathPrefix.fieldValue};SASToken=${dataTargetFormData.sasToken.fieldValue};`

        break;

      default:
        connDetails =
          `{'endpoint':'${dataTargetFormData.endpoint.fieldValue}', 'database':'${dataTargetFormData.database.fieldValue}',` +
          `'port':'${dataTargetFormData.port.fieldValue}', 'userName':'${dataTargetFormData.username.fieldValue}'}`;

        break;
    }

    setConnectionDetailsValue(connDetails);

    return connDetails;
  };

  // Setup payload for POST test connection endpoint

  const getConnectionPayload = () => {
    let payload = {
      type: functions.generateTestConnectionType(connection),
      appConnectionString: functions.generateConnectionString(
        connection,
        constants.CONN_STRING_TYPE.app,
        dataTargetFormData.endpoint.fieldValue,
        dataTargetFormData.database.fieldValue,
        dataTargetFormData.port.fieldValue,
        dataTargetFormData.username.fieldValue,
        dataTargetFormData.password.fieldValue,
        dataTargetFormData.serviceName.fieldValue
      ),
      synapseConnectionString: functions.generateConnectionString(
        connection,
        constants.CONN_STRING_TYPE.synapse,
        dataTargetFormData.endpoint.fieldValue,
        dataTargetFormData.database.fieldValue,
        dataTargetFormData.port.fieldValue,
        dataTargetFormData.username.fieldValue,
        dataTargetFormData.password.fieldValue,
        dataTargetFormData.serviceName.fieldValue
      ),
    };

    return payload;
  };

  // Create a new data target
  // Called when the Save button is clickedd

  function createNewTarget() {
    if (
      connection === constants.CONNECTION_TYPES.s3Buckets ||
      connection === constants.CONNECTION_TYPES.azure
    ) {
      setConnectionDetails(connection);
      validate();
    }
    else {
      submitDataTarget();
    }

  }

  function submitDataTarget() {
    if (mode === constants.MODE.add) {

      // Setup payload for POST data target endpoint
      let secretValue: ISecret[] = connection === constants.CONNECTION_TYPES.redShift ? functions.generateRedshiftSecret(
        connection,
        dataTargetFormData.secretName.fieldValue,
        dataTargetFormData.endpoint.fieldValue,
        dataTargetFormData.database.fieldValue,
        dataTargetFormData.port.fieldValue,
        dataTargetFormData.username.fieldValue,
        dataTargetFormData.password.fieldValue,
        dataTargetFormData.accountKey.fieldValue,
        dataTargetFormData.accountName.fieldValue,
        dataTargetFormData.container.fieldValue,
        dataTargetFormData.sasToken.fieldValue
      ) : functions.generateSecretArray(
        connection,
        dataTargetFormData.secretName.fieldValue,
        dataTargetFormData.endpoint.fieldValue,
        dataTargetFormData.database.fieldValue,
        dataTargetFormData.port.fieldValue,
        dataTargetFormData.username.fieldValue,
        dataTargetFormData.password.fieldValue,
        dataTargetFormData.accountKey.fieldValue,
        dataTargetFormData.accountName.fieldValue,
        dataTargetFormData.container.fieldValue,
        dataTargetFormData.sasToken.fieldValue
      );

      let getConnectionDetails = connectionDetailsValue !== ""
        ? connectionDetailsValue
        : setConnectionDetails(connection)

      let payload = {
        name: dataTargetFormData.name.fieldValue,
        layerType: functions.generateLayerTypeTarget(connection),
        connectionDetails: getConnectionDetails,
        secretName: dataTargetFormData.secretName.fieldValue,
        secrets: secretValue,
        criticality: dataTargetFormData.criticality.fieldValue,
        connectionString: connection === "S3 Buckets" ? dataTargetFormData.kmsId.fieldValue : connection === constants.CONNECTION_TYPES.azure ? getConnectionDetails : '',
        businessMetadata: {
          description: dataTargetFormData.description.fieldValue,
          owner: dataTargetFormData.owner.fieldValue,
          rttmsCI: dataTargetFormData.rttmsCi.fieldValue,
          userAccessList: functions.getStewardsIDString(userAccessIDs),
          rttmsReferenceNumber: dataTargetFormData.rttmsRefNumber.fieldValue,
          costCentre: dataTargetFormData.costCenter.fieldValue,
          projectCode: dataTargetFormData.projectCode.fieldValue,
          internalOrder: dataTargetFormData.internalOrder.fieldValue,
        },
      };
      dispatch(actions.postDataTargetRequest(payload));

      setTargetNavigation(true);
    }
    //editing a target
    if (mode === constants.MODE.edit) {
      // Setup payload for POST data target endpoint
      // let secretValue: ISecret[] = functions.generateSecretArray(
      //   connection,
      //   dataTargetFormData.secretName.fieldValue,
      //   dataTargetFormData.endpoint.fieldValue,
      //   dataTargetFormData.database.fieldValue,
      //   dataTargetFormData.port.fieldValue,
      //   dataTargetFormData.username.fieldValue,
      //   dataTargetFormData.password.fieldValue
      // );
      let payload = {
        name: dataTargetFormData.name.fieldValue,
        layerType: functions.generateLayerTypeTarget(connection),
        connectionDetails: targetDetails?.connectionDetails!,
        secretName: dataTargetFormData.secretName.fieldValue,
        criticality: dataTargetFormData.criticality.fieldValue,
        // secrets: secretValue,
        businessMetadata: {
          description: dataTargetFormData.description.fieldValue,
          owner: dataTargetFormData.owner.fieldValue,
          rttmsCI: dataTargetFormData.rttmsCi.fieldValue,
          // userAccessList: userAccessList!?.toString(),
          userAccessList: functions.getStewardsIDString(userAccessIDs),
          rttmsReferenceNumber: dataTargetFormData.rttmsRefNumber.fieldValue,
          costCentre: dataTargetFormData.costCenter.fieldValue,
          projectCode: dataTargetFormData.projectCode.fieldValue,
          internalOrder: dataTargetFormData.internalOrder.fieldValue,
        },
      };
      dispatch(actions.putDataTargetRequest(Number(targetId), payload));
      setTargetNavigation(true);
    }
  }

  const editTarget = (event: React.MouseEvent<HTMLButtonElement>) => {
    const url = `/EditDataTarget/${targetId}`;
    if (event.ctrlKey) {
      window.open(url, '_blank')
    }
    else {
      navigate(url);
    }
  };


  return (
    <Formik initialValues={{}} onSubmit={() => { }}>
      <div>

        <Stack direction="row" spacing={1}>
          <Button id="4ba65f8b-3135-4b2f-8022-0c021b862943" aria-label="delete" onClick={goBack} startIcon={<ArrowBackIosNewIcon />}>
            Back
          </Button>
        </Stack>
        <Grid container>
          <Grid item xs={6}>
            <h1 style={{ textAlign: "left" }}>{mode} Data Target</h1>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" justifyContent="flex-end" m={2}>
              {subscriptionPending ? <LoadingButton loading loadingPosition="start" style={{ marginTop: '1em' }} variant="outlined" size="small" startIcon={<SaveIcon />} > Loading </LoadingButton>
                :
                <>
                  {!subscribed ?
                    <Button id="3621ffdc-cb22-4825-88ff-8fcf2119a5ec"
                      startIcon={<NotificationsIcon />}
                      color="primary"
                      variant="contained"
                      onClick={subscriptionClicked}
                      style={{ marginRight: "1em" }}
                    >
                      Subscribe
                    </Button> : ''}
                  {subscribed ?
                    <Button id="e5d5cc87-5296-4543-b1f4-fdc8092b5253"
                      startIcon={<NotificationsActiveIcon />}
                      color="inherit"
                      variant="contained"
                      onClick={subscriptionDeleted}
                      style={{ marginRight: "1em" }}
                    >
                      Subscribed
                    </Button>
                    : ''
                  }
                </>
              }
            </Box>
          </Grid>
        </Grid>


        <Form>
          {/* adding skeleton screen on loading */}
          {(mode === constants.MODE.view || mode === constants.MODE.edit) &&
            loadTargetDetails ? (
            <Box>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={4}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={12}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
                <Grid item xs={6}>
                  <Skeleton animation="wave" height={60} />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Grid container spacing={4}>
              <Grid item xs={5}>
                <DataField id="cce21f98-f0c4-4e6d-83b8-d8acee33d300"
                  label="Name"
                  name="name"
                  required={true}
                  value={dataTargetFormData.name.fieldValue}
                  onChange={handleChange}
                  error={dataTargetFormData.name.isError}
                  disabled={mode === constants.MODE.view || mode === constants.MODE.edit ? true : false}
                  readOnly={mode === constants.MODE.view ? true : false}
                />

              </Grid>

              <Grid item xs={7}>
                <DataField id="01f304f3-cb26-4002-972f-81c456d387a2"
                  type="textarea"
                  name="description"
                  label="Description"
                  required={true}
                  multiline={true}
                  rows={mode === "View" ? 1 : 5}
                  readOnly={mode === constants.MODE.view ? true : false}
                  value={dataTargetFormData.description.fieldValue}
                  onChange={handleChange}
                  error={dataTargetFormData.description.isError}
                  disabled={mode === constants.MODE.view ? true : false}
                />
              </Grid>
              <Grid item xs={12} >
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  disabled={mode === constants.MODE.view ? true : false}
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                  required
                >
                  <InputLabel id="demo-simple-select-label">Criticality</InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="criticality-simple-select"
                    label="Criticality"
                    name="criticality"
                    onChange={handleDropdownChange}
                    autoWidth
                    value={dataTargetFormData.criticality.fieldValue}
                  >
                    {constants.criticalities.map((item, index) => {
                      return (
                        <MenuItem
                          value={item.value}
                          key={index}
                          sx={{ borderTop: 1, borderColor: "grey.400" }}
                        >
                          <ListItemText
                            primary={item.title}
                            secondary={
                              <>
                                {item.application}
                                <br />
                                {item.availability}
                                <br />
                                {item.upTime}
                              </>
                            }
                          />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                {mode !== constants.MODE.view && (
                  <FormControl
                    sx={{ m: 1 }}
                    fullWidth
                    variant={
                      mode === constants.MODE.view ? "standard" : "outlined"
                    }
                  >                    <Autocomplete
                      options={idpUsersProduction}
                      onChange={(event, value) => {
                        let v = value as IUser;
                        dataTargetFormData.owner.fieldValue =
                          v === null ? "" : v.userName;
                        setOwnerValue(v === null ? "" : v.userName);
                      }}
                      getOptionLabel={(option) =>
                        option === undefined ? " " : option.userName
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.userName === value.userName
                      }
                      value={functions.getIUser(
                        dataTargetFormData.owner.fieldValue
                      )}
                      disabled={mode === constants.MODE.view ? true : false}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="Owner"
                          label="Owner"
                          required
                          value={dataTargetFormData.owner.fieldValue}
                          error={dataTargetFormData.owner.isError}
                        />
                      )}
                    />
                  </FormControl>
                )}

                {mode === constants.MODE.view && (
                  <DataField id="a1460cdb-8e6b-497b-b031-8f774f21e15a"
                    label="Owner"
                    name="owner"
                    value={dataTargetFormData.owner.fieldValue}
                    disabled={true}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                )}
              </Grid>
              <Grid item xs={4}>
                {mode !== constants.MODE.view && (
                  <FormControl
                    sx={{ m: 1 }}
                    fullWidth
                    variant={
                      mode === constants.MODE.view ? "standard" : "outlined"
                    }
                  >
                    <Autocomplete
                      options={idpUsersProduction}
                      multiple
                      onChange={(event, value) => {
                        let v = value as IUser[];
                        setUserAccessIDs(v);

                        if (v) {
                          dataTargetFormData.userAccessList.isError =
                            v.length === 0 ? true : false;
                        } else {
                          dataTargetFormData.userAccessList.isError = true;
                        }
                      }}
                      getOptionLabel={(option) =>
                        option === undefined ? "" : option.userName
                      }
                      disabled={mode === constants.MODE.view ? true : false}
                      value={userAccessIDs ?? []}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          name="userAccessList"
                          label="User Access List"
                          value={dataTargetFormData.userAccessList.fieldValue}
                          error={dataTargetFormData.userAccessList.isError}
                        />
                      )}

                    />
                  </FormControl>
                )}
                {mode === constants.MODE.view && (
                  <DataField id="bd1acdb4-aa4c-42f8-a852-8fd101f1f30a"
                    label="User Access List"
                    name="userAccessList"
                    value={dataTargetFormData.userAccessList.fieldValue}
                    disabled={true}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                )}
              </Grid>
              <Grid item xs={4}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="65d89677-32ae-46ce-8f8d-82156ac5f129"
                    label="Cost Center"
                    name="costCenter"
                    type="text"
                    value={dataTargetFormData.costCenter.fieldValue}
                    onChange={handleChange}
                    error={dataTargetFormData.costCenter.isError}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="51cbe342-6e50-4238-8303-5a1bf4c1c0a6"
                    label="Project Code"
                    name="projectCode"
                    type="text"
                    value={dataTargetFormData.projectCode.fieldValue}
                    onChange={handleChange}
                    error={dataTargetFormData.projectCode.isError}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="4cdd1f1d-1b83-404b-946a-04551be798e3"
                    label="Internal Order"
                    name="internalOrder"
                    type="text"
                    value={dataTargetFormData.internalOrder.fieldValue}
                    onChange={handleChange}
                    error={dataTargetFormData.internalOrder.isError}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="f8887057-bc7e-4b87-b6b9-0de8fcd04b8c"
                    label="RTTMS Reference Number"
                    name="rttmsRefNumber"
                    type="text"
                    value={dataTargetFormData.rttmsRefNumber.fieldValue}
                    onChange={handleChange}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={4}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="0c651710-6db4-46f3-86b4-88ab58c5617e"
                    label="RTTMS CI"
                    name="rttmsCi"
                    type="text"
                    value={dataTargetFormData.rttmsCi.fieldValue}
                    onChange={handleChange}
                    disabled={mode === constants.MODE.view ? true : false}
                    readOnly={mode === constants.MODE.view ? true : false}
                  />
                </FormControl>
              </Grid>

              {mode !== constants.MODE.view && (
                <Grid item xs={3}>
                  <FormControl
                    sx={{ m: 1 }}
                    fullWidth
                    variant={
                      mode === constants.MODE.view ? "standard" : "outlined"
                    }
                  >
                    <Picker
                      label="Connection Type"
                      name="connectionType"
                      options={[
                        constants.CONNECTION_TYPES.mssql,
                        constants.CONNECTION_TYPES.postGres,
                        constants.CONNECTION_TYPES.redShift,
                        constants.CONNECTION_TYPES.s3Buckets,
                        constants.CONNECTION_TYPES.azure,
                      ]}
                      onChange={changeConnection}
                      value={connection}
                      disabled={mode === constants.MODE.add ? false : true}
                    />
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={5}>
                <FormControl
                  sx={{ m: 1 }}
                  fullWidth
                  variant={
                    mode === constants.MODE.view ? "standard" : "outlined"
                  }
                >
                  <DataField id="7770e002-9456-4527-a195-e79df8e10eaa"
                    label="Secret Name"
                    name="secretName"
                    required={true}
                    value={
                      mode === constants.MODE.add
                        ? secretNameValue
                        : dataTargetFormData.secretName.fieldValue
                    }
                    onChange={handleChange}
                    readOnly={mode === constants.MODE.view ? true : false}
                    infoText="The value of secret name will be auto-generated"
                    disabled={true}
                  />
                </FormControl>
              </Grid>


              {mode === constants.MODE.add && (
                <Grid item xs={12}>
                  <Box display="flex">
                    <ConnectionS3Bucket
                      selectedConnection={connection}
                      onChange={handleChange}
                      s3Bucket={dataTargetFormData.s3Bucket}
                      s3Path={dataTargetFormData.s3Path}
                      kmsId={dataTargetFormData.kmsId}
                    />
                    <ConnectionAzure
                      selectedConnection={connection}
                      onChange={handleChange}
                      onDropdownChange={handleDropdownChange}
                      accountName={dataTargetFormData.accountName}
                      container={dataTargetFormData.container}
                      pathPrefix={dataTargetFormData.pathPrefix}
                      sasToken={dataTargetFormData.sasToken}
                      accountKey={dataTargetFormData.accountKey}
                    />
                    <ConnectionDatabase
                      selectedConnection={connection}
                      onChange={handleChange}
                      endpoint={dataTargetFormData.endpoint}
                      database={dataTargetFormData.database}
                      extractSource={dataTargetFormData.extractSource}
                      port={dataTargetFormData.port}
                      instanceName={dataTargetFormData.instanceName}
                      username={dataTargetFormData.username}
                      password={dataTargetFormData.password}
                      serviceName={dataTargetFormData.serviceName}
                    />
                  </Box>
                </Grid>
              )}

              {/* Displaying connection details on view */}
              {mode === constants.MODE.view && (
                <>
                  <Grid item xs={12}>
                    {/* <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="61ed5f10-bed2-4764-a5d2-41dc4ec26b3f"
                            label="Connection Details"
                            name="connectiondetails"
                            value={connectionDetailsValue}
                            readOnly={
                              formMode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl> */}
                  </Grid>
                  {connection === "MSSQL" || connection === "Redshift" || connection === "Postgres" ?
                    <>
                      <Grid item xs={4}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="6605f89f-b406-4207-ba45-572761618949"
                            label="Endpoint"
                            name="endpoint"
                            value={endpoint}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="f435b5c0-1a52-4977-a4c0-e5631f490c2c"
                            label="Database"
                            name="database"
                            value={database}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>

                      {connection !== "Redshift" ?
                        <>
                          <Grid item xs={2}>
                            <FormControl
                              sx={{ m: 1 }}
                              fullWidth
                              variant={
                                mode === constants.MODE.view ? "standard" : "outlined"
                              }
                            >
                              <DataField id="e3c6b0f4-8ad7-439f-af92-32077f49dcf0"
                                label="Port"
                                name="port"
                                value={port}
                                readOnly={
                                  mode === constants.MODE.view ? true : false
                                }
                                disabled={true}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={3}>
                            <FormControl
                              sx={{ m: 1 }}
                              fullWidth
                              variant={
                                mode === constants.MODE.view ? "standard" : "outlined"
                              }
                            >
                              <DataField id="b77a5fec-70b8-4544-a5dd-fa141c55ac90"
                                label="Username"
                                name="username"
                                value={username}
                                readOnly={
                                  mode === constants.MODE.view ? true : false
                                }
                                disabled={true}
                              />
                            </FormControl>
                          </Grid>
                        </>
                        : ''}
                    </>
                    : ""}
                  {connection === "S3 Buckets" ?
                    <>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="543e9bad-9cc3-4862-bdb2-89917ec71ae1"
                            label="Bucket"
                            name="bucket"
                            value={bucket}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="e1b2f5b2-77cc-4a78-b030-92bb70c63db1"
                            label="Path"
                            name="path"
                            value={path}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                    </>
                    : ""}
                  {connection === "Windows Fileshare" ?
                    <>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="c90da0b0-b0fd-48d6-bf95-b37e08486cde"
                            label="Share Name"
                            name="shareName"
                            value={shareName}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="ae2ec8c2-79d7-4624-82a1-d0cc4512ee85"
                            label="Username"
                            name="username"
                            value={username}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={3}>
                        <FormControl
                          sx={{ m: 1 }}
                          fullWidth
                          variant={
                            mode === constants.MODE.view ? "standard" : "outlined"
                          }
                        >
                          <DataField id="2d2ee249-a49e-4e94-9d90-96bde3897af9"
                            label="Path"
                            name="path"
                            value={path}
                            readOnly={
                              mode === constants.MODE.view ? true : false
                            }
                            disabled={true}
                          />
                        </FormControl>
                      </Grid>
                    </>
                    : ""}

                </>
              )}

              {mode === "View" ? (
                <>
                  <Grid item xs={12}></Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="flex-start">
                      Last Updated By: {lastUpdatedBy}
                    </Box>
                    <Box display="flex" justifyContent="flex-start">
                      Last Updated On: {lastUpdatedOn}
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box display="flex" justifyContent="flex-end">
                      Created By: {createdBy}
                    </Box>
                    <Box display="flex" justifyContent="flex-end">
                      Created On: {createdOn}
                    </Box>
                  </Grid>
                </>
              ) : (
                ""
              )}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" m={2}>
                  {mode === constants.MODE.view && (
                    <Button id="c3809176-0ff5-45ea-8cef-7f1a7e2a6d68"
                      color="primary"
                      variant="contained"
                      onClick={viewTargets}
                      style={{ marginRight: "1em" }}
                    >
                      View Loaders
                    </Button>
                  )}
                  {mode !== constants.MODE.view && (
                    <Button id="14e666ed-39a8-48d7-b5d0-d803474a4b91"
                      color="primary"
                      variant="contained"
                      onClick={goBack}
                      style={{ marginRight: "1em" }}
                    >
                      Cancel
                    </Button>
                  )}
                  {displayTestBtn &&
                    mode === constants.MODE.add &&
                    (postConnectionPending && loadIcon ? (
                      <LoadingButton
                        loading
                        variant="outlined"
                        size="small"
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        style={{ marginRight: "1em" }}
                      >
                        {" "}
                        Processing{" "}
                      </LoadingButton>
                    ) : (
                      <Button id="755c4d31-ea8d-433c-a6eb-ad54b537642f"
                        color="primary"
                        variant="contained"
                        onClick={testConnection}
                        style={{ marginRight: "1em" }}
                      >
                        Test Connection{" "}
                      </Button>
                    ))}
                  {(displayEditBtn || isAdmin) && mode === constants.MODE.view && (
                    <Button id="ca69b470-a10d-4898-be66-2513118f07c6"
                      color="primary"
                      variant="contained"
                      onClick={editTarget}
                      style={{ marginRight: "1em" }}
                    >
                      Edit
                    </Button>
                  )}
                  {displaySaveBtn && (
                    <Button id="1e698209-a48b-42d8-a2d5-ca1f95e8abff"
                      color="primary"
                      variant="contained"
                      onClick={createNewTarget}
                      disabled={mode === constants.MODE.add}
                      style={{ marginRight: "1em" }}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </Form>
      </div>
    </Formik >
  );
};

export default DataTargetForm;
